import qs from 'qs';

const LOCAL_STORAGE_KEY = 'mindbodyWidgetSelector';

const readSelector = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const parseIds = (raw: string): string[] =>
  raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

/**
 * Capture the `widgetId` selector at app boot — call once in index.tsx BEFORE React mounts,
 * while the launch URL query is still intact (the SDK redirect strips it before any page renders).
 *
 * Gated on the host-supplied `authToken` so it acts ONLY on a FRESH LAUNCH:
 *  - Fresh launch with a `widgetId` → persist it (the utility button targeted specific widgets).
 *  - Fresh launch with no `widgetId` → clear, so the building's full widget set shows (and a stale
 *    selection from a different button doesn't leak in).
 *  - The post-redirect hard reload has an empty query; the guard makes it a no-op so the captured
 *    selector survives.
 */
export const captureWidgetSelector = (): void => {
  const routeParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  if (!routeParams.authToken) {
    return;
  }

  const widgetId = readSelector(routeParams.widgetId);

  if (widgetId) {
    localStorage.setItem(LOCAL_STORAGE_KEY, widgetId);

    return;
  }

  localStorage.removeItem(LOCAL_STORAGE_KEY);
};

/**
 * The widget ids this launch should display, parsed from the comma-listed `widgetId` param.
 * Prefers the live URL (local dev / pre-redirect), falling back to the boot-persisted copy.
 * An empty array means "no selection" → the caller shows all of the building's widgets.
 */
export const useWidgetSelector = (): string[] => {
  const routeParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const urlSelector = readSelector(routeParams.widgetId);

  if (urlSelector) {
    return parseIds(urlSelector);
  }

  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);

  return stored ? parseIds(stored) : [];
};
